<template>
  <div v-if="complaint" class="summary-panel">
    <!-- Header -->
    <div class="summary-header p-3">
      <div class="d-flex justify-content-between align-items-center">
        <div class="d-flex align-items-center">
          <h4 class="mb-0 mr-2">Protocolo: {{ complaint.protocol }}</h4>
        </div>
        <b-button variant="primary" @click="goToComplaint"
          >Acessar Denúncia Completa</b-button
        >
      </div>
    </div>

    <!-- Body -->
    <div class="summary-body p-3">
      <b-row class="info-grid mb-4">
        <b-col md="6">
          <div class="info-item">
            <small
              class="text-muted"
              style="font-size: 1.1rem; font-weight: 600"
              >Data de Recebimento</small
            >
            <p class="mb-0">{{ complaint.createdAt | datetime }}</p>
          </div>
        </b-col>
        <b-col md="6">
          <div class="info-item">
            <small
              class="text-muted"
              style="font-size: 1.1rem; font-weight: 600"
              >Última Movimentação</small
            >
            <p class="mb-0">{{ complaint.updatedAt | datetime }}</p>
          </div>
        </b-col>
      </b-row>

      <div class="section-card mb-4">
        <h6 class="section-title" style="font-size: 1.2rem; font-weight: 600">
          {{
            isAiSummaryEnabled ? "Resumo Anonimizado" : "Descrição da Denúncia"
          }}
        </h6>

        <!-- Se IA está habilitada -->
        <template v-if="isAiSummaryEnabled">
          <!-- Se já tem resumo, mostra o resumo -->
          <template v-if="localSummary">
            <b-alert variant="warning" show class="mb-3">
              <i class="fas fa-exclamation-triangle mr-2"></i>
              Este resumo foi gerado por Inteligência Artificial e não passou
              por validação humana.
            </b-alert>
            <p class="description-text">
              {{ localSummary }}
            </p>
          </template>

          <!-- Se não tem resumo, mostra opção de gerar -->
          <template v-else>
            <div class="ai-generate-container text-center py-4">
              <!-- Ícone de robô -->
              <div class="ai-icon mb-3">
                <i
                  class="fas fa-robot"
                  style="font-size: 3rem; color: #6c757d"
                ></i>
              </div>

              <h5 class="mb-2" style="color: #495057">
                Resumo Anonimizado com IA
              </h5>

              <p
                class="text-muted mb-4"
                style="max-width: 400px; margin: 0 auto"
              >
                O resumo anonimizado ainda não foi gerado para esta denúncia.
                Clique no botão abaixo para gerar automaticamente utilizando
                Inteligência Artificial.
              </p>

              <!-- Loading durante geração -->
              <div v-if="generatingSummary" class="mb-3">
                <b-spinner variant="primary" class="mr-2"></b-spinner>
                <span class="text-primary">Gerando resumo com IA...</span>
              </div>

              <!-- Botão de gerar -->
              <b-button
                v-else
                variant="primary"
                :disabled="!complaint.text"
                @click="generateSummary"
              >
                <i class="fas fa-magic mr-2"></i>
                Gerar Resumo com IA
              </b-button>

              <p v-if="!complaint.text" class="text-muted mt-2 small">
                <i class="fas fa-info-circle mr-1"></i>
                Não é possível gerar resumo: denúncia sem texto.
              </p>
            </div>
          </template>
        </template>

        <!-- Se IA está desabilitada, mostra denúncia original -->
        <template v-else>
          <p
            v-if="complaint.text || complaint.description"
            class="description-text"
          >
            {{ complaint.text || complaint.description }}
          </p>
          <p v-else class="description-text text-muted">
            Descrição não disponível
          </p>
        </template>
      </div>

      <div
        v-if="
          complaint.committee &&
          (complaint.committee.admin ||
            (complaint.committee.users && complaint.committee.users.length))
        "
        class="section-card mb-4"
      >
        <h6 class="section-title" style="font-size: 1.2rem; font-weight: 600">
          Comissão de Apuração
        </h6>
        <b-list-group flush>
          <b-list-group-item
            v-if="complaint.committee.admin"
            class="d-flex justify-content-between align-items-center"
          >
            <div>
              <i class="fas fa-user-tie mr-2 text-primary"></i>
              {{ getCommitteeUser(complaint.committee.admin).name }}
              {{ getCommitteeUser(complaint.committee.admin).surname }}
            </div>
            <b-badge variant="primary" pill>Relator</b-badge>
          </b-list-group-item>
          <b-list-group-item
            v-for="user in complaint.committee.users"
            :key="user._id"
            class="d-flex justify-content-between align-items-center"
          >
            <div>
              <i class="fas fa-user mr-2 text-muted"></i>
              {{ getCommitteeUser(user).name }}
              {{ getCommitteeUser(user).surname }}
            </div>
          </b-list-group-item>
        </b-list-group>
      </div>
    </div>

    <!-- Footer removed: action moved to header -->
  </div>
</template>

<script>
import { getBadgeColor, getStatusText, labelEventType } from "./utils";
import { getCompanyUsers } from "@/api/company";
import { getSystemUsers } from "@/api/user";
import { generateAISummary } from "@/api/complaint";
import EventType from "../../../../backend/models/EventType.ts";
import { mapGetters } from "vuex";

export default {
  name: "ComplaintSummary",
  props: {
    complaint: {
      type: Object,
      required: true,
    },
    events: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      companyUsers: {},
      managedByCompanyUsers: {},
      conformitaUsers: {},
      EventType,
      generatingSummary: false,
      localSummary: null,
    };
  },
  computed: {
    ...mapGetters(["classificationMaps"]),
    isAiSummaryEnabled() {
      // Por padrão, considera habilitado (true) se não estiver definido
      return (
        this.complaint?.company?.modules?.complaint?.aiSummaryEnabled !== false
      );
    },
  },
  watch: {
    complaint: {
      handler(newVal) {
        this.loadUsers();
        this.localSummary = newVal?.summary || null;
      },
      immediate: true,
    },
  },
  async created() {
    if (this.complaint) {
      await this.loadUsers();
      this.localSummary = this.complaint.summary || null;
    }
  },
  methods: {
    getBadgeColor,
    getStatusText,
    labelEventType,
    goToComplaint() {
      this.$router.push({ path: `/denuncias/${this.complaint._id}` });
    },
    closeSidebar() {
      this.$emit("close");
    },
    async generateSummary() {
      if (!this.complaint?._id) return;

      this.generatingSummary = true;

      try {
        const response = await generateAISummary(this.complaint._id);

        if (response.data?.summary) {
          this.localSummary = response.data.summary;
          this.$bvToast.toast("Resumo anonimizado gerado com sucesso!", {
            title: "Sucesso",
            toastClass: "custom-toast custom-toast-success",
            variant: "light",
            solid: true,
            autoHideDelay: 5000,
            noCloseButton: false,
          });
        }
      } catch (error) {
        console.error("Erro ao gerar resumo:", error);
        const errorMessage =
          error.response?.data ||
          "Erro ao gerar resumo com IA. Tente novamente.";
        this.$bvToast.toast(errorMessage, {
          title: "Erro",
          toastClass: "custom-toast custom-toast-error",
          variant: "light",
          solid: true,
          autoHideDelay: 10000,
          noCloseButton: false,
        });
      } finally {
        this.generatingSummary = false;
      }
    },
    getCommitteeUser(userId) {
      if (this.companyUsers[userId]) {
        return this.companyUsers[userId];
      }
      if (
        this.complaint.company.managedBy &&
        this.managedByCompanyUsers[userId]
      ) {
        return this.managedByCompanyUsers[userId];
      }
      return (
        this.conformitaUsers[userId] || {
          name: "Usuário",
          surname: "Desconhecido",
        }
      );
    },
    async loadUsers() {
      try {
        if (this.complaint.company._id) {
          let resp = await getCompanyUsers(this.complaint.company._id, true);
          this.companyUsers = resp.data.reduce((obj, user) => {
            obj[user["_id"]] = user;
            return obj;
          }, {});
        }
      } catch (e) {
        console.warn("Não foi possível carregar os usuários da empresa");
      }

      try {
        if (this.complaint.company.managedBy) {
          let managedCompanyUsers = (
            await getCompanyUsers(this.complaint.company.managedBy, true)
          ).data;
          this.managedByCompanyUsers = managedCompanyUsers.reduce(
            (obj, user) => {
              obj[user["_id"]] = user;
              return obj;
            },
            {}
          );
        }
      } catch (e) {
        console.warn(
          "Não foi possível carregar os usuários da empresa gestora"
        );
      }

      try {
        let resp = await getSystemUsers();
        this.conformitaUsers = resp.data.reduce((obj, user) => {
          obj[user["_id"]] = user;
          return obj;
        }, {});
      } catch (e) {
        console.warn("Não foi possível carregar os usuários do sistema");
      }
    },

    eventCommitteeAddedList(committee) {
      const result = committee.new.users
        .filter((user) => !committee.old.users.includes(user))
        .map(
          (user) =>
            `${this.getCommitteeUser(user).name} ${
              this.getCommitteeUser(user).surname
            } (comitê)`
        );
      if (committee.new.admin && committee.new.admin !== committee.old.admin) {
        const user = committee.new.admin;
        result.push(
          `${this.getCommitteeUser(user).name} ${
            this.getCommitteeUser(user).surname
          } (relator)`
        );
      }
      return result.join(", ");
    },

    eventCommitteeRemovedList(committee) {
      const result = committee.old.users
        .filter((user) => !committee.new.users.includes(user))
        .map(
          (user) =>
            `${this.getCommitteeUser(user).name} ${
              this.getCommitteeUser(user).surname
            } (comitê)`
        );
      if (committee.old.admin && committee.old.admin !== committee.new.admin) {
        const user = committee.old.admin;
        result.push(
          `${this.getCommitteeUser(user).name} ${
            this.getCommitteeUser(user).surname
          } (relator)`
        );
      }
      return result.join(", ");
    },

    eventCommitteeCurrent(committee) {
      const result = committee.new.users.map(
        (user) =>
          `${this.getCommitteeUser(user).name} ${
            this.getCommitteeUser(user).surname
          } (comitê)`
      );
      if (committee.new.admin) {
        const user = committee.new.admin;
        result.push(
          `${this.getCommitteeUser(user).name} ${
            this.getCommitteeUser(user).surname
          } (relator)`
        );
      }
      if (result.length === 0) {
        return "Sem comissão";
      }
      return result.join(", ");
    },
  },
};
</script>

<style scoped>
.summary-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: #f7f9fc;
}

.summary-header {
  background-color: #ffffff;
  border-bottom: 1px solid #e9ecef;
}

.protocol-text {
  color: #333;
}

.status-badge {
  font-size: 0.85rem;
  padding: 0.4em 0.8em;
  font-weight: 600;
}

.summary-body {
  flex-grow: 1;
  overflow-y: auto;
}

.info-grid .info-item {
  background-color: #fff;
  padding: 1rem;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  height: 100%;
}

.section-card {
  background-color: #fff;
  padding: 1.25rem;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.section-title {
  font-weight: 600;
  margin-bottom: 1rem;
  color: #0056b3;
}

.description-text {
  white-space: pre-wrap;
  font-size: 0.95rem;
  line-height: 1.6;
  color: #555;
}

.ai-generate-container {
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 2px dashed #dee2e6;
  padding: 2rem;
}

.ai-icon {
  opacity: 0.7;
}

.list-group-item {
  border: none;
  padding-left: 0;
  padding-right: 0;
}

.event-item {
  border-bottom: 1px solid #e9ecef;
}

.event-item:last-child {
  border-bottom: none;
}

.event-description {
  color: #6c757d;
  font-size: 0.9rem;
}

/* footer styles removed as button moved to header */
</style>
